import { useState } from 'react';
import { useGetFinanceAnalytic } from '@/api/hooks/useFinanceAnalytic';
import { PeriodType } from '@/api/gql/graphql';
import { useCustomFilterStore } from '@/store/CustomFilterStore/CustomFilter';
import { useFormatDate } from '@/hooks/useFormatDate';

export const useFinance = () => {
  const { selectedDateRange, isFilterApplied } = useCustomFilterStore();
  const { formatDateUs } = useFormatDate();
  const { data: financeData, isLoading: loadingFinanceAnalytic } =
    useGetFinanceAnalytic({
      period: isFilterApplied ? PeriodType.Custom : PeriodType.All,
      startDate:
        isFilterApplied && selectedDateRange
          ? formatDateUs(selectedDateRange[0])
          : null,
      endDate:
        isFilterApplied && selectedDateRange
          ? formatDateUs(selectedDateRange[1])
          : null,
    });

  const [activeTab, setActiveTab] = useState(0);
  const [currentPageTitle, setCurrentPageTitle] = useState('');

  const subscriptionRevenues = {
    daily: financeData?.data?.financialAnalytic?.subscriptionRevenues?.daily,
    weekly: financeData?.data?.financialAnalytic?.subscriptionRevenues?.weekly,
    monthly:
      financeData?.data?.financialAnalytic?.subscriptionRevenues?.monthly,
    quarterly:
      financeData?.data?.financialAnalytic?.subscriptionRevenues?.quarterly,
    yearly: financeData?.data?.financialAnalytic?.subscriptionRevenues?.yearly,
    totalTransactionAmount:
      financeData?.data?.financialAnalytic?.totalTransactionAmount,
  };

  const salesDistribution = {
    daily: financeData?.data?.financialAnalytic?.salesDistribution?.daily,
    weekly: financeData?.data?.financialAnalytic?.salesDistribution?.weekly,
    monthly: financeData?.data?.financialAnalytic?.salesDistribution?.monthly,
    quarterly:
      financeData?.data?.financialAnalytic?.salesDistribution?.quarterly,
  };

  const conversion = {
    rate:
      financeData?.data?.financialAnalytic?.conversionMetrics?.conversionRate ||
      0,
    convertedCustomers:
      financeData?.data?.financialAnalytic?.conversionMetrics
        ?.convertedToPayingCustomers || 0,
    totalFreeTrials:
      financeData?.data?.financialAnalytic?.conversionMetrics
        ?.totalFreeTrials || 0,
  };

  const premium = {
    current:
      financeData?.data?.financialAnalytic?.premiumMetrics?.currentlyPremium ||
      0,
    exPremium:
      financeData?.data?.financialAnalytic?.premiumMetrics?.totalExPremium || 0,
  };

  const resubscription = {
    rate:
      financeData?.data?.financialAnalytic?.resubscriptionMetrics
        ?.resubscriptionRate || 0,
    totalUsers:
      financeData?.data?.financialAnalytic?.resubscriptionMetrics
        ?.totalPremiumUsers || 0,
    resubscribed:
      financeData?.data?.financialAnalytic?.resubscriptionMetrics
        ?.usersWhoResubscribed || 0,
    distribution:
      financeData?.data?.financialAnalytic?.resubscriptionMetrics
        ?.distributionChart || [],
  };

  const users = {
    freeCandidates: financeData?.data?.userDistribution?.freeCandidates || 0,
    freeMode: financeData?.data?.userDistribution?.freeMode || 0,
    freeTrialMode: financeData?.data?.userDistribution?.freeTrialMode || 0,
    premiumMode: financeData?.data?.userDistribution?.premiumMode || 0,
  };

  const trends = financeData?.data?.financialAnalytic?.revenueTrendChart || [];

  return {
    subscriptionRevenues,
    salesDistribution,
    conversion,
    premium,
    resubscription,
    users,
    trends,
    isLoading: loadingFinanceAnalytic,
    activeTab,
    currentPageTitle,
    setActiveTab,
    setCurrentPageTitle,
  };
};
